/**
 * The frame: one status strip on top, the screen in the middle, one thumb-height tab row at the
 * bottom. Navigation lives at the bottom because this is used one-handed while holding a basket,
 * and the top of a modern phone is not reachable with the thumb that is holding it.
 */
import { euros, isoDay, percent } from "./format.ts";
import { navigate, useRoute, type Route } from "./hooks.ts";
import { useModel } from "./model.tsx";
import { api } from "./api.ts";
import { Icon, type IconName } from "./components/icons.tsx";
import { Accounts } from "./screens/Accounts.tsx";
import { Category } from "./screens/Category.tsx";
import { History } from "./screens/History.tsx";
import { Result } from "./screens/Result.tsx";
import { Scan } from "./screens/Scan.tsx";
import { Login } from "./screens/Login.tsx";
import { Offers } from "./screens/Offers.tsx";

const TABS: { route: Route; icon: IconName; label: string }[] = [
	{ route: "scan", icon: "scan", label: "Scan" },
	{ route: "category", icon: "tag", label: "Trip" },
	{ route: "result", icon: "card", label: "Card" },
	{ route: "accounts", icon: "cards", label: "Pool" },
	{ route: "offers", icon: "gift", label: "Offers" },
	{ route: "history", icon: "clock", label: "History" },
];

export function App() {
	const route = useRoute();
	const model = useModel();
	const { basket, rec, boot } = model;
	if (model.authExpired || window.location.pathname === "/login") return <Login />;

	const chosen =
		basket?.categoryId == null ? null : (model.categories.find((c) => c.id === basket.categoryId)?.name ?? null);
	const guess = rec !== null && rec.categorySource === "INFERRED" ? rec.categoryName : null;

	return (
		<div className="app">
			<header className="topbar">
				<span className="brand">
					Bonus<b>box</b>
				</span>
				<span className="topbar-week">
					{boot === null
						? "\u2014"
						: `${isoDay(boot.week.startDate)} \u2013 ${isoDay(boot.week.endDate)}`}
				</span>
				<button
					type="button"
					className="userchip"
					title={`Sign out ${boot?.principal.displayName ?? ""}`}
					aria-label={`Sign out ${boot?.principal.displayName ?? "current user"}`}
					onClick={() => {
						void api.logout().finally(() => window.location.replace("/login"));
					}}
				>
					{boot?.principal.displayName.split(/\s+/)[0] ?? "user"}
				</button>
				<button
					type="button"
					className={chosen !== null ? "tripchip tripchip-set" : guess !== null ? "tripchip tripchip-guess" : "tripchip"}
					onClick={() => navigate("category")}
				>
					<Icon name="tag" />
					{chosen ?? (guess === null ? "no category" : `${guess}?`)}
					{chosen === null && guess !== null && rec?.categoryConfidence != null && (
						<small>{percent(rec.categoryConfidence)}</small>
					)}
				</button>
			</header>


			{model.bootError !== null && !model.authExpired && (
				<div className="banner banner-danger" role="alert">
					<Icon name="alert" />
					<span>{model.bootError}</span>
					<button type="button" className="btn btn-quiet" onClick={model.reloadBoot}>
						Retry
					</button>
				</div>
			)}

			{basket !== null && basket.committedTripId !== null && route !== "result" && (
				<div className="banner banner-accent">
					<Icon name="check" />
					<span>This basket is committed as trip #{basket.committedTripId}.</span>
					<button type="button" className="btn btn-quiet" onClick={() => void model.startNewBasket()}>
						New basket
					</button>
				</div>
			)}

			{route === "scan" && <Scan />}
			{route === "category" && <Category />}
			{route === "result" && <Result />}
			{route === "accounts" && <Accounts />}
			{route === "offers" && <Offers />}
			{route === "history" && <History />}

			<nav className="tabbar" aria-label="Sections">
				{TABS.map((tab) => (
					<button
						key={tab.route}
						type="button"
						className={route === tab.route ? "tab tab-on" : "tab"}
						aria-current={route === tab.route ? "page" : undefined}
						onClick={() => navigate(tab.route)}
					>
						<Icon name={tab.icon} />
						<span>{tab.label}</span>
						{tab.route === "scan" && basket !== null && basket.lines.length > 0 && (
							<i className="tab-count">{basket.lines.length}</i>
						)}
						{tab.route === "result" && rec?.winner != null && (
							<i className="tab-money">{euros(rec.winner.plan.discountCents)}</i>
						)}
					</button>
				))}
			</nav>
		</div>
	);
}
